import type { Appointment } from "@dentacore/shared-types";

import { useCan } from "../auth/use-can";
import { useModules } from "../shell/modules-context";

/**
 * How a block looks, as a **name rather than a look**.
 *
 * ── Why this is a separate type ─────────────────────────────────────────────
 *
 * STORY-032 is asked to leave a seam here: *the grid's block renderer takes a
 * state, so STORY-056 can add pending invitations without rewriting it*
 * (ADR-0004, whose rendering reasoning ADR-0005 inherits). This is that seam,
 * and it is only real if the component never asks an appointment what it looks
 * like — only what state it is in.
 *
 * So the mapping from data to state lives in `stateOf`, the mapping from state
 * to pixels lives in one CSS class per state, and **adding a state is a case
 * here plus a class there.** Nothing in the grid branches on a status.
 *
 * ── What is deliberately absent ─────────────────────────────────────────────
 *
 * The mockup draws a **new patient — first visit** treatment in the warm
 * family. Nothing in the payload says whether a visit is a patient's first, and
 * deriving it would be a query no story has asked for, so that state is not
 * invented here — the warm family belongs to `awaiting` instead, which is a
 * distinction the data actually carries.
 *
 * **No `cancelled` state, and since T2 no `no-show` state either.** A cancelled
 * appointment drew as a dashed, transparent ghost until 2026-08-24 — matching
 * the mockup and working plan §6.1's "immediately free" rule taken literally as
 * a rendering choice, not just an occupancy one. Asked directly, the call was
 * that a cancelled slot should look exactly like any other free time: nothing
 * drawn at all.
 *
 * **A no-show joined it on 2026-08-31**, for a reason that was a live defect
 * rather than a matter of taste: both statuses free the chair — they share
 * `NON_BLOCKING_STATUSES` — so a booking made into a missed slot was accepted
 * and then drawn straight on top of the struck-through block still sitting
 * there. The client's phrase was "they can even intertwine".
 *
 * The caller (`day-grid.tsx`) filters on `blocksTheChair` before a block ever
 * reaches this function, so `stateOf` is never asked to name either state.
 * **Neither is defined here**, which is the point: a state that exists in the
 * type and cannot be reached is a trap for whoever reads it next.
 */
export type BlockState =
  | "booked"
  | "confirmed"
  | "checked-in"
  | "in-chair"
  | "completed"
  | "awaiting"
  | "held"
  | "no-show";

/**
 * Which state an appointment is in.
 *
 * ── Five statuses used to collapse into one (QA #12) ────────────────────────
 *
 * This mapped all seven statuses onto four states, so NOT_CHECKED_IN,
 * CONFIRMED, CHECKED_IN, COMPLETED and NO_SHOW every one of them drew as
 * `booked` — identical fill, identical border, identical text. Working plan
 * §6.2 asks for the opposite, and names the pairing that matters most:
 * **"Confirmed and Checked in must never look alike."** They were pixel-equal.
 *
 * The seam the file was built around is what made fixing it small: four new
 * cases here and four rules in the stylesheet, with nothing in the grid
 * component learning what a status looks like.
 *
 * ── The order of these checks is the meaning ────────────────────────────────
 *
 * `held` and `awaiting` still come first, and neither is a status: one is the
 * absence of a patient, the other is ADR-0005's "no human has looked at this
 * yet". Both say something the status cannot, so both keep outranking it.
 *
 * **CANCELLED is absent, deliberately.** `day-grid.tsx` draws nothing for it
 * (2026-08-24), so there is no state to name.
 *
 * **NO_SHOW is drawn again since FX28** (reversing T2): struck through, never
 * holding the chair, and narrowed to a strip whenever a live booking shares its
 * time — which is what stops the two "intertwining", T2's original complaint.
 */
export function stateOf(appointment: Appointment, awaitingShown: boolean): BlockState {
  // A block has nobody in it — the chair held for maintenance, a meeting or a
  // break — and it turns over at zero, which is what lets a booking sit flush
  // against it. It is not a patient's appointment and must not read as one.
  if (appointment.patient === null) return "held";

  // Booked by the patient over WhatsApp and not yet looked at by staff
  // (ADR-0005). It is a real appointment holding a real slot, drawn apart so
  // the front desk can see what still needs a human eye.
  //
  // **Only with Chatbot on, to someone who may validate it** (QF-D9, QF-D21):
  // otherwise it is an ordinary booking, drawn by its status like any other —
  // a label nobody can act on, for a module the clinic no longer has, is noise.
  if (appointment.awaitingValidation && awaitingShown) return "awaiting";

  if (appointment.status === "NO_SHOW") return "no-show";

  switch (appointment.status) {
    case "IN_CHAIR":
      return "in-chair";
    case "CHECKED_IN":
      return "checked-in";
    case "CONFIRMED":
      return "confirmed";
    case "COMPLETED":
      return "completed";
    default:
      // NOT_CHECKED_IN — the state a booking is made in, and the ordinary one.
      // It keeps the chair's own colour family, set inline by the grid.
      return "booked";
  }
}

/**
 * Whether a WhatsApp booking's *Awaiting validation* is drawn at all — the
 * grid's label and the panel's Validate and Decline (QF-D9, QF-D21). Needs
 * Chatbot on and `appointments.validate`; without either the booking reads as
 * the ordinary booked appointment it already is.
 */
export function useAwaitingShown(): boolean {
  const hasChatbot = useModules().has("CHATBOT");
  const can = useCan();
  return hasChatbot && can("appointments.validate");
}
